import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { mapPath, routes } from "./authMapData";

const appear = keyframes`from { opacity: 0 } to { opacity: 1 }`;

const Map = styled.div`
  width: 100%;
  margin-top: 16px;
  > svg {
    animation: ${appear} 0.5s ease-out;
    display: block;
    width: 100%;
    height: auto;
    max-height: 380px;
  }
`;
const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 12px;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 14px;
  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
`;

// Одна короткая партия на Dust II: Т идут на A, CT ротируются с B, бомба ставится,
// CT пытается разминировать, последний Т не успевает убежать от взрыва.
// В каждой дуэли стреляют оба, но умирает только один, и все смерти в разные моменты.
// Контур и маршруты в authMapData.ts выведены из радарной схемы, всё считается от времени в render(t).
const LOOP = 40;
const COLOR = { t: "#e9a475", ct: "#8faec9" };
const BOMB = { x: 316, y: 72, plantFrom: 16.4, plantTo: 19.4, defuseFrom: 27.9, defuseTime: 10, boom: 34.5 };

type Wp = [number, number, number]; // время, x, y
type Id = "t1" | "t2" | "t3" | "t4" | "t5" | "ct1" | "ct2" | "ct3" | "ct4" | "ct5";
interface Unit {
  team: "t" | "ct";
  route: Wp[];
  dies: number;
}

const deaths: Record<Id, number> = {
  t1: 10.4, ct1: 11.8, t2: 13.8, ct2: 15.2, t4: 21.2, ct3: 22.8, t5: 24.6, ct4: 26.2, ct5: 30.4, t3: 35,
};
const units = Object.fromEntries(
  (Object.keys(deaths) as Id[]).map((id) => [
    id,
    { team: id.startsWith("ct") ? "ct" : "t", route: routes[id], dies: deaths[id] },
  ]),
) as Record<Id, Unit>;
const ids = Object.keys(units) as Id[];

interface Shot {
  from: Id;
  to: Id;
  a: number;
  b: number;
}
// Стреляют оба; очередь победителя заканчивается смертью цели, очередь проигравшего обрывается его смертью.
const shots: Shot[] = [
  { from: "t1", to: "ct1", a: 9.2, b: 10.4 },
  { from: "ct1", to: "t1", a: 9, b: 10.4 },
  { from: "ct1", to: "t2", a: 11.1, b: 11.8 },
  { from: "t2", to: "ct1", a: 11, b: 11.8 },
  { from: "t2", to: "ct2", a: 13, b: 13.8 },
  { from: "ct2", to: "t2", a: 12.8, b: 13.8 },
  { from: "ct2", to: "t3", a: 14.5, b: 15.2 },
  { from: "t3", to: "ct2", a: 14.4, b: 15.2 },
  { from: "t4", to: "ct3", a: 20.2, b: 21.2 },
  { from: "ct3", to: "t4", a: 20, b: 21.2 },
  { from: "ct3", to: "t5", a: 21.8, b: 22.8 },
  { from: "t5", to: "ct3", a: 21.6, b: 22.8 },
  { from: "t5", to: "ct4", a: 23.6, b: 24.6 },
  { from: "ct4", to: "t5", a: 23.4, b: 24.6 },
  { from: "ct4", to: "t3", a: 25.3, b: 26.2 },
  { from: "t3", to: "ct4", a: 25, b: 26.2 },
  { from: "t3", to: "ct5", a: 28.2, b: 30.4 },
];

function at(route: Wp[], t: number): [number, number] {
  if (t <= route[0][0]) return [route[0][1], route[0][2]];
  for (let i = 1; i < route.length; i++) {
    const [t1, x1, y1] = route[i];
    if (t <= t1) {
      const [t0, x0, y0] = route[i - 1];
      const k = t1 === t0 ? 1 : (t - t0) / (t1 - t0);
      return [x0 + (x1 - x0) * k, y0 + (y1 - y0) * k];
    }
  }
  const last = route[route.length - 1];
  return [last[1], last[2]];
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const RING = 2 * Math.PI * 10;

export default function AuthMap() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const el: Record<string, SVGElement> = {};
    svg.querySelectorAll<SVGElement>("[data-k]").forEach((n) => {
      el[n.dataset.k as string] = n;
    });
    const set = (k: string, attrs: Record<string, string | number>) => {
      const n = el[k];
      for (const a in attrs) n.setAttribute(a, String(attrs[a]));
    };
    const pos = (id: Id, t: number) => at(units[id].route, Math.min(t, units[id].dies));
    const alive = (id: Id, t: number) => t < units[id].dies;

    const render = (t: number) => {
      set("scene", { opacity: clamp01(Math.min(t / 0.6, (LOOP - t) / 1.4)) });

      for (const id of ids) {
        const u = units[id];
        const [x, y] = pos(id, t);
        const live = alive(id, t);
        set(`u-${id}`, { transform: `translate(${x.toFixed(1)} ${y.toFixed(1)})` });
        set(`u-${id}-live`, { display: live ? "inline" : "none" });
        set(`u-${id}-dead`, { display: live ? "none" : "inline" });
        const k = clamp01((t - u.dies) / 0.7);
        set(`u-${id}-pulse`, {
          r: 3.4 + 10 * k,
          opacity: t >= u.dies ? (1 - k) * 0.7 : 0,
        });
      }

      shots.forEach((s, i) => {
        const end = Math.min(s.b, units[s.from].dies);
        const on = t >= s.a && t < end && Math.floor((t - s.a) * 9) % 2 === 0;
        if (!on) {
          set(`s-${i}`, { opacity: 0 });
          set(`s-${i}-hit`, { opacity: 0 });
          return;
        }
        const [x1, y1] = pos(s.from, t);
        const [x2, y2] = pos(s.to, t);
        set(`s-${i}`, { x1, y1, x2, y2, opacity: 0.95 });
        set(`s-${i}-hit`, { cx: x2, cy: y2, opacity: 0.9 });
      });

      // бомба: установка, тиканье, разминирование, взрыв
      const planting = t >= BOMB.plantFrom && t < BOMB.plantTo;
      const planted = t >= BOMB.plantTo && t < BOMB.boom;
      const defusing = t >= BOMB.defuseFrom && alive("ct5", t);
      set("plant", {
        opacity: planting ? 1 : 0,
        "stroke-dashoffset": RING * (1 - clamp01((t - BOMB.plantFrom) / (BOMB.plantTo - BOMB.plantFrom))),
      });
      set("defuse", {
        opacity: defusing ? 1 : 0,
        "stroke-dashoffset": RING * (1 - clamp01((t - BOMB.defuseFrom) / BOMB.defuseTime)),
      });
      set("bomb", { opacity: planted ? 1 : 0 });
      if (planted) {
        const tau = t - BOMB.plantTo;
        const span = BOMB.boom - BOMB.plantTo;
        const phase = (tau + (1.5 * tau * tau) / span) % 1;
        const k = clamp01(phase / 0.4);
        set("bomb-led", { opacity: phase < 0.22 ? 1 : 0.25 });
        set("bomb-pulse", { r: 5 + 18 * k, opacity: phase < 0.4 ? (1 - k) * 0.8 : 0 });
      }
      const u = (t - BOMB.boom) / 1.6;
      if (u >= 0 && u < 1) {
        const e = 1 - Math.pow(1 - u, 3);
        set("boom", { r: 5 + 125 * e, opacity: (1 - u) * 0.95, "stroke-width": 3 - 2 * u });
        set("boom-flash", { r: 6 + 66 * e, opacity: Math.max(0, 1 - u * 2.4) * 0.7 });
      } else {
        set("boom", { opacity: 0 });
        set("boom-flash", { opacity: 0 });
      }
    };

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      render(29.2);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      render(((now - start) / 1000) % LOOP);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Map>
      <svg
        ref={svgRef}
        viewBox="0 0 400 400"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path d={mapPath} fill="#252a32" fillRule="evenodd" stroke="#444b56" strokeWidth="1.6" strokeLinejoin="round" />
        <g fill="#adb5c1" fontSize="15" fontWeight="600" opacity=".5" textAnchor="middle">
          <text x="82" y="57">B</text>
          <text x="317" y="67">A</text>
        </g>

        <g data-k="scene" opacity="0">
          <g transform={`translate(${BOMB.x} ${BOMB.y})`}>
            <circle
              data-k="plant"
              r="10"
              stroke="#ff985b"
              strokeWidth="1.6"
              strokeDasharray={RING}
              strokeDashoffset={RING}
              transform="rotate(-90)"
              opacity="0"
            />
            <circle
              data-k="defuse"
              r="10"
              stroke="#8faec9"
              strokeWidth="1.6"
              strokeDasharray={RING}
              strokeDashoffset={RING}
              transform="rotate(-90)"
              opacity="0"
            />
            <g data-k="bomb" opacity="0">
              <circle data-k="bomb-pulse" r="5" stroke="#ff985b" strokeWidth="1.2" opacity="0" />
              <rect x="-4" y="-2.8" width="8" height="5.6" rx="1.2" fill="#d9503f" stroke="#171c23" strokeWidth=".8" />
              <circle data-k="bomb-led" r="1.2" fill="#fff1e6" />
            </g>
          </g>

          {ids.map((id) => (
            <g key={id} data-k={`u-${id}`} color={COLOR[units[id].team]}>
              <circle data-k={`u-${id}-pulse`} r="3" stroke="currentColor" strokeWidth="1.2" opacity="0" />
              <g data-k={`u-${id}-live`}>
                <circle r="8" fill="currentColor" opacity=".08" />
                <circle r="3.4" fill="currentColor" stroke="#171c23" strokeWidth="1.2" />
              </g>
              <path
                data-k={`u-${id}-dead`}
                display="none"
                d="M-2.6 -2.6L2.6 2.6M2.6 -2.6L-2.6 2.6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity=".5"
              />
            </g>
          ))}

          {shots.map((s, i) => (
            <g key={i} color={COLOR[units[s.from].team]}>
              <line data-k={`s-${i}`} stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0" />
              <circle data-k={`s-${i}-hit`} r="1.7" fill="#fff1e6" opacity="0" />
            </g>
          ))}

          <g transform={`translate(${BOMB.x} ${BOMB.y})`}>
            <circle data-k="boom-flash" r="6" fill="#ffd9bd" opacity="0" />
            <circle data-k="boom" r="5" fill="#ff985b22" stroke="#ff985b" strokeWidth="3" opacity="0" />
          </g>
        </g>
      </svg>
      <Legend>
        <span>
          <i style={{ background: "#8faec9" }} />
          CT
        </span>
        <span>
          <i style={{ background: "#e9a475" }} />T
        </span>
      </Legend>
    </Map>
  );
}
